import { RunContextApp } from "framework/RCApp";
import { IngestionConfigsResponse, SaveProjectConfigData, ProjectEnvironment } from "types/apis/pilotInterfaces";
import { toast } from '../utils/toast';

// Centralized Error Handling Utility
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: any;
}

export interface ErrorHandlingOptions {
  showToast?: boolean;
  showAlert?: boolean;
  context?: string;
  fallbackMessage?: string;
}

export class ErrorHandler {
  /**
   * Handles API errors in a consistent way across the application
   * @param error - The error object from API response or catch block
   * @param options - Configuration options for error handling
   * @returns Formatted error message
   */
  static handleApiError(
    error: any, 
    options: ErrorHandlingOptions = {}
  ): string {
    const {
      showToast = true,
      showAlert = true,
      context = 'operation',
      fallbackMessage = 'An unexpected error occurred'
    } = options;

    console.error(`${context} error:`, error);
    
    // Extract error message from different response formats
    let errorMessage = fallbackMessage;
    let errorType: 'error' | 'warning' = 'error';
    
    if (error && typeof error === 'object') {
      // Handle API error response format
      if (error.message) {
        errorMessage = error.message;
      } else if (error.error && error.error.message) {
        // Handle nested error structure
        errorMessage = error.error.message;
      } else if (error.details) {
        // Handle validation errors
        errorMessage = Array.isArray(error.details) 
          ? error.details.join(', ')
          : error.details;
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // Categorize error types for appropriate handling
    const categorizedError = this.categorizeError(errorMessage);
    errorMessage = categorizedError.message;
    errorType = categorizedError.type;

    // Show toast notification if enabled
    if (showToast) {
      this.showToast(errorMessage, errorType);
    }

    return errorMessage;
  }

  /**
   * Categorizes errors based on message content for appropriate handling
   */
  private static categorizeError(message: string): { message: string; type: 'error' | 'warning' } {
    const lowerMessage = message.toLowerCase();
    
    // Duplicate/conflict errors
    if (lowerMessage.includes('already exists') || 
        lowerMessage.includes('duplicate') || 
        lowerMessage.includes('conflict')) {
      return {
        message: `Operation failed: ${message}. Please try with different parameters.`,
        type: 'warning'
      };
    }

    // Validation errors
    if (lowerMessage.includes('validation') || 
        lowerMessage.includes('invalid') || 
        lowerMessage.includes('required') ||
        lowerMessage.includes('format')) {
      return {
        message: `Validation error: ${message}`,
        type: 'warning'
      };
    }

    // Permission/authorization errors
    if (lowerMessage.includes('permission') || 
        lowerMessage.includes('unauthorized') || 
        lowerMessage.includes('forbidden') ||
        lowerMessage.includes('access denied')) {
      return {
        message: `Access denied: ${message}`,
        type: 'error'
      };
    }

    // Network/connection errors
    if (lowerMessage.includes('network') || 
        lowerMessage.includes('connection') || 
        lowerMessage.includes('fetch')) {
      return {
        message: 'Network error: Please check your connection and try again.',
        type: 'error'
      };
    }

    // Timeout errors
    if (lowerMessage.includes('timeout') || 
        lowerMessage.includes('timed out')) {
      return {
        message: 'Request timeout: Please try again.',
        type: 'warning'
      };
    }

    // Server errors
    if (lowerMessage.includes('server error') || 
        lowerMessage.includes('internal error') ||
        lowerMessage.includes('500')) {
      return {
        message: 'Server error: Please try again later.',
        type: 'error'
      };
    }

    // Not found errors
    if (lowerMessage.includes('not found') || 
        lowerMessage.includes('404')) {
      return {
        message: `Resource not found: ${message}`,
        type: 'warning'
      };
    }

    // Rate limiting
    if (lowerMessage.includes('rate limit') || 
        lowerMessage.includes('too many requests')) {
      return {
        message: 'Too many requests: Please wait a moment and try again.',
        type: 'warning'
      };
    }

    // Default case
    return {
      message: message,
      type: 'error'
    };
  }

  /**
   * Shows appropriate toast notification based on error type
   */
  private static showToast(message: string, type: 'error' | 'warning'): void {
    switch (type) {
      case 'warning':
        toast.warning(message);
        break;
      default:
        toast.error(message);
        break;
    }
  }

  /**
   * Handles project-specific errors with custom messages
   */
  static handleProjectError(error: any, operation: 'create' | 'update' | 'delete' | 'fetch' = 'create'): string {
    const context = `Project ${operation}`;
    const fallbackMessage = `Project ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }

  /**
   * Handles configuration-specific errors
   */
  static handleConfigError(error: any, operation: 'save' | 'load' | 'update' = 'save'): string {
    const context = `Configuration ${operation}`;
    const fallbackMessage = `Configuration ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }

  /**
   * Handles environment-specific errors
   */
  static handleEnvironmentError(error: any, operation: 'create' | 'update' | 'delete' = 'create'): string {
    const context = `Environment ${operation}`;
    const fallbackMessage = `Environment ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }

  /**
   * Handles workflow-specific errors
   */
  static handleWorkflowError(error: any, operation: 'create' | 'update' | 'delete' | 'execute' = 'create'): string {
    const context = `Workflow ${operation}`;
    const fallbackMessage = `Workflow ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }

  /**
   * Handles recommendation-specific errors
   */
  static handleRecommendationError(error: any, operation: 'create' | 'update' | 'delete' | 'upload' = 'create'): string {
    const context = `Recommendation ${operation}`;
    const fallbackMessage = `Recommendation ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }
}

// Export a simple function for backward compatibility
export const handleApiError = (error: any, context?: string): string => {
  return ErrorHandler.handleApiError(error, { context });
};

// Pilot interface with environment support
export interface PilotWithEnvironment {
  id: string;
  name: string;
  description: string;
  projectId: string;
  environment?: ProjectEnvironment; // New single environment object
  projectEnvironments?: ProjectEnvironment[]; // Legacy support for backward compatibility
  projectType: string;
  [key: string]: any;
}


export interface PendingUatConfig {
  id: string;
  projectId: string;
  configType: string;
  configKey: string;
  configDataType?: string;
  configDocumentation?: string;
  configLevel: string;
  db_value: string; // new value in DB
  pingpong_value: string; // old/baseline value
}

export const PilotApis = {

/*--------------------------------- BFF APIs ---------------------------------*/


  getPilots : async (rc : RunContextApp) : Promise<any> => {
    
    const projectsResp = await rc.bffClient.getProjects();
    
    // Check if the response has an error property (error object)
    if ('error' in projectsResp && projectsResp.error) {
      return {
        error: projectsResp.error
      };
    }

    // Handle successful AxiosResponse
    const apiData = (projectsResp as any).data?.data;
   
    return {
      data: {
        data: apiData,
        meta: {
          pagination: {
            page: 1,
            pageSize: 25,
            pageCount: 1,
            total: apiData.length
          }
        }
      } as any,
      error: null
    };
  },

  getStrapiProjects : async (rc : RunContextApp) : Promise<any> => {
    
    const strapiProjectsResp = await rc.bffClient.getStrapiProjects();
    
    // Check if the response has an error property (error object)
    if ('error' in strapiProjectsResp && strapiProjectsResp.error) {
      return {
        error: strapiProjectsResp.error
      };
    }

    // Handle successful AxiosResponse
    // The API returns { data: [...] } directly, not nested in data.data
    const apiData = (strapiProjectsResp as any).data?.data || (strapiProjectsResp as any).data || [];
   
    return {
      data: {
        data: apiData,
        meta: {
          pagination: {
            page: 1,
            pageSize: 25,
            pageCount: 1,
            total: apiData?.length || 0
          }
        }
      } as any,
      error: null
    };
  },

  getUtilities : async (rc : RunContextApp) : Promise<any> => {
    
    const projectsResp = await rc.bffClient.getUtilities();
    
    // Check if the response has an error property (error object)
    if ('error' in projectsResp && projectsResp.error) {
      return {
        error: projectsResp.error
      };
    }

    // Handle successful AxiosResponse
    const apiData = (projectsResp as any).data?.data;

    // Transform the API response to match the expected format
    return {
      data: {
        data: apiData,
        meta: {
          pagination: {
            page: 1,
            pageSize: 25,
            pageCount: 1,
            total: apiData.length
          }
        }
      },
      error: null
    };
  },

  getEnvironments : async (rc : RunContextApp, utilityId : number) : Promise<any> => {
    
    const environmentsResp = await rc.bffClient.getEnvironments(utilityId);
    
    // Check if the response has an error property (error object)
    if ('error' in environmentsResp && environmentsResp.error) {
      return {
        error: environmentsResp.error
      };
    }

    // Handle successful AxiosResponse
    const apiData = (environmentsResp as any).data?.data;

    // Transform the API response to match the expected format
    return {
      data: {
        data: apiData,
        meta: {
          pagination: {
            page: 1,
            pageSize: 25,
            pageCount: 1,
            total: apiData.length
          }
        }
      },
      error: null
    };
  },

  getAllEnvironments : async (rc : RunContextApp) : Promise<any> => {
    
    const allEnvironmentsResp = await rc.bffClient.getAllEnvironments();
    
    // Check if the response has an error property (error object)
    if ('error' in allEnvironmentsResp && allEnvironmentsResp.error) {
      return {
        error: allEnvironmentsResp.error
      };
    }

    // Handle successful AxiosResponse
    const apiData = (allEnvironmentsResp as any).data?.data;

    // Transform the API response to match the expected format
    return {
      data: {
        data: apiData,
        meta: {
          pagination: {
            page: 1,
            pageSize: 25,
            pageCount: 1,
            total: apiData.length
          }
        }
      },
      error: null
    };
  },

  getAssociatedEnvironments : async (rc : RunContextApp, projectId : string) : Promise<any> => {
    
    const associatedEnvironmentsResp = await rc.bffClient.getAssociatedEnvironments(projectId);
    
    // Check if the response has an error property (error object)
    if ('error' in associatedEnvironmentsResp && associatedEnvironmentsResp.error) {
      return {
        error: associatedEnvironmentsResp.error
      };
    }

    // Handle successful AxiosResponse
    const apiData = (associatedEnvironmentsResp as any).data;

    // Transform the API response to match the expected format
    return {
      data: {
        data: apiData?.environments,
        meta: {
          pagination: {
            page: 1,
            pageSize: 25,
            pageCount: 1,
            total: apiData?.length || 0
          }
        }
      },
      error: null
    };
  },

  associateEnvironment : async (rc : RunContextApp, projectId : string, environmentId : string) : Promise<any> => {
    
    const associateEnvironmentResp = await rc.bffClient.associateEnvironment(projectId, environmentId);
    
    // Check if the response has an error property (error object)
    if ('error' in associateEnvironmentResp && associateEnvironmentResp.error) {
      return {
        error: associateEnvironmentResp.error
      };
    }

    // Handle successful AxiosResponse
    const apiData = (associateEnvironmentResp as any).data;

    // Transform the API response to match the expected format
    return {
      data: apiData,
      error: null
    };
  },

  createPilot : async (rc : RunContextApp, projectData : any) : Promise<any> => {
    
    const createResp = await rc.bffClient.createProject(projectData);
    
    if ('error' in createResp && createResp.error) {
      return {
        data: null,
        error: createResp.error
      };
    }

    return {
      data: (createResp as any).data,
      error: null
    };
  },

  createProjectAndPushConfigs : async (rc : RunContextApp, projectId : string) : Promise<any> => {
    
    const createResp = await rc.bffClient.createProjectAndPushConfigs(projectId);
    
    if ('error' in createResp && createResp.error) {
      return {
        data: null,
        error: createResp.error
      };
    }

    return {
      data: (createResp as any).data,
      error: null
    };
  },

  createRecoProject : async (rc : RunContextApp, projectId: string, sourcePilotId: string) : Promise<any> => {
    
    const createResp = await rc.bffClient.createRecoProject(projectId, sourcePilotId);
    
    if ('error' in createResp && createResp.error) {
      return {
        data: null,
        error: createResp.error
      };
    }

    return {
      data: (createResp as any).data,
      error: null
    };
  },

  createUtility : async (rc : RunContextApp, utilityData : { name: string }) : Promise<any> => {
    
    const createResp = await rc.bffClient.createUtility(utilityData);
    
    if ('error' in createResp && createResp.error) {
      return {
        data: null,
        error: createResp.error
      };
    }

    return {
      data: (createResp as any).data,
      error: null
    };
  },

  createEnvironment : async (rc : RunContextApp, projectId : string, environmentData : { utilityName: string; environmentName: string }) : Promise<any> => {
    
    const createResp = await rc.bffClient.createEnvironment(projectId, environmentData);
    
    if ('error' in createResp && createResp.error) {
      return {
        data: (createResp as any).data,
        error: createResp.error
      };
    }

    return {
      data:(createResp as any).data,
      error: null
    };
  },


  updatePilot : async (rc : RunContextApp, projectId : string, projectData : any) : Promise<any> => {
    
    const updateResp = await rc.bffClient.updateProject(projectId, projectData);
    
    if ('error' in updateResp && updateResp.error) {
      return {
        data: null,
        error: updateResp.error
      };
    }

    return {
      data: (updateResp as any).data,
      error: null
    };
  },

  updateProjectEnvironment : async (rc : RunContextApp, projectId : string, environmentId : number) : Promise<any> => {
    
    const updateResp = await rc.bffClient.updateProject(projectId, { environment_id: environmentId });
    
    if ('error' in updateResp && updateResp.error) {
      return {
        data: null,
        error: updateResp.error
      };
    }

    return {
      data: (updateResp as any).data,
      error: null
    };
  },

  checkEnvironmentProcessStatus : async (rc : RunContextApp,projectId : string, processId : string) : Promise<any> => {
    
    const statusResp = await rc.bffClient.checkProcessStatus(projectId, processId);
    
    if ('error' in statusResp && statusResp.error) {
      return {
        data: null,
        error: statusResp.error
      };
    }

    return {
      data: (statusResp as any)?.data?.data,
      error: null
    };
  },

  updateProjectEnvironmentStatus : async (rc : RunContextApp, projectId : string, environmentId : string, status : string) : Promise<any> => {
    
    // Get the current project data first
    const projectsResp = await rc.bffClient.getProjects();
    
    if ('error' in projectsResp && projectsResp.error) {
      return {
        data: null,
        error: projectsResp.error
      };
    }

    const projects = (projectsResp as any).data?.data;
    const project = projects?.find((p: any) => p.projectId === projectId);
    
    if (!project) {
      return {
        data: null,
        error: { message: 'Project not found' }
      };
    }

    // Handle both new environment structure and legacy projectEnvironments
    let updateData: any = { projectId: projectId };
    
    if (project.environment) {
      // New structure: update single environment object
      if (project.environment.id === environmentId) {
        updateData.environment = {
          ...project.environment,
          status: status
        };
      }
    } else if (project.projectEnvironments && Array.isArray(project.projectEnvironments)) {
      // Legacy structure: update projectEnvironments array
      const updatedProjectEnvironments = project.projectEnvironments.map((env: any) => {
        if (env.id === environmentId) {
          return {
            ...env,
            status: status
          };
        }
        return env;
      });
      updateData.projectEnvironments = updatedProjectEnvironments;
    }
    
    const updateResp = await rc.bffClient.updateProject(projectId, updateData);
    
    if ('error' in updateResp && updateResp.error) {
      return {
        data: null,
        error: updateResp.error
      };
    }

    // Return the updated project data for Redux store update
    const updatedProject = {
      ...project,
      ...updateData
    };

    return {
      data: updatedProject,
      error: null
    };
  },

  getIngestionConfigs : async (rc : RunContextApp, pilotId : string) : Promise<IngestionConfigsResponse> => {
    try {
      const resp = await rc.bffClient.getIngestionConfigs(pilotId)
      
      // Check if response has error property (from catch block)
      if ('error' in resp && resp.error) {
        return {
          success: false,
          data: [],
          error: resp.error
        }
      }
      
      // Handle axios response - the API returns array directly
      if ('data' in resp && resp.data) {
        // The API returns the array directly, not wrapped in a data property
        const configData = Array.isArray(resp.data) ? resp.data : resp.data.data || [];
        return {
          success: true,
          data: configData,
          error: undefined
        }
      }
      
      return {
        success: true,
        data: [],
        error: undefined
      }
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /v1/ingestion_configs/${pilotId}\n` + 
        `\nresulted in error : \n`, error)
      
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  },

  saveProjectConfigs : async (rc : RunContextApp, configsData: SaveProjectConfigData[]) : Promise<{ success: boolean; error?: string }> => {
    try {
      const resp = await rc.bffClient.saveProjectConfigs(configsData)
      
      // Check if response has error property (from catch block)
      if ('error' in resp && resp.error) {
        return {
          success: false,
          error: resp.error
        }
      }
      
      // Handle axios response
      if ('data' in resp && resp.data) {
        return {
          success: true,
          error: undefined
        }
      }
      
      return {
        success: true,
        error: undefined
      }
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : /v1/save_project_config\n` + 
        `\nresulted in error : \n`, error)
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  },

  // Keep the old method for backward compatibility
  saveProjectConfig : async (rc : RunContextApp, configData: SaveProjectConfigData) : Promise<{ success: boolean; error?: string }> => {
    return PilotApis.saveProjectConfigs(rc, [configData]);
  },
  

  getConfigRegistry : async (rc : RunContextApp) : Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const resp = await rc.bffClient.getConfigRegistry()
      
      // Check if response has error property (from catch block)
      if ('error' in resp && resp.error) {
        return {
          success: false,
          data: undefined,
          error: resp.error
        }
      }
      
      // Handle axios response
      if ('data' in resp && resp.data) {
        return {
          success: true,
          data: resp.data,
          error: undefined
        }
      }
      
      return {
        success: true,
        data: undefined,
        error: undefined
      }
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /v1/get_config_registry\n` + 
        `\nresulted in error : \n`, error)
      
      return {
        success: false,
        data: undefined,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
    
  },

  async getApplicationsMetadata(rc: RunContextApp, api_host: string, project_id: string) {
    try {
      const response = await rc.bffClient.getApplicationsMetadata(api_host, project_id);
      
      // Check if response has error property (error case)
      if ('error' in response) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error
      };
    }
  },

  async getProjectAttributes(rc: RunContextApp, project_id: string) {
    try {
      const response = await rc.bffClient.getProjectAttributes(project_id);
      
      // Check if response has error property (error case)
      if ('error' in response) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error
      };
    }
  },

  async createConfigTypes(rc: RunContextApp, api_host: string, configData: any) {
    try {
      const response = await rc.bffClient.createConfigTypes(api_host, configData);
      
      // Check if response has error property (error case)
      if ('error' in response) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error
      };
    }
  },

  getPilotConfigsWithFilters: async (rc: RunContextApp, projectId: string, apiHost: string, application?: string, fuelType?: string): Promise<IngestionConfigsResponse> => {
    try {
      const response = await rc.bffClient.getPilotConfigsWithFilters(projectId, apiHost, application, fuelType);
      
      // Check if response has error property (from catch block)
      if ('error' in response && response.error) {
        return {
          success: false,
          data: [],
          error: response.error
        }
      }
      
      // Handle axios response - the API returns array directly
      if ('data' in response && response.data) {
        // The API returns the array directly, not wrapped in a data property
        const configData = Array.isArray(response.data) ? response.data : response.data.data || [];
        return {
          success: true,
          data: configData,
          error: undefined
        }
      }
      
      return {
        success: true,
        data: [],
        error: undefined
      }
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : getPilotConfigsWithFilters\n` + 
        `\nresulted in error : \n`, error)
      
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  },

  predictConfigs: async (rc: RunContextApp, formData: any): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.predictConfigs(formData);
      
      // Check if response has error property (from catch block)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        }
      }
      
      // Handle axios response
      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        }
      }
      
      return {
        success: true,
        data: response
      }
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : predict-configs\n` + 
        `\nresulted in error : \n`, error)
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  },

  pushConfigsToUat: async (rc: RunContextApp, projectId: string): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.pushConfigsToUat(projectId);

      // Check if response has error property (from catch block in BffApiClient)
      if (response && typeof response === 'object' && 'error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      // If axios response, check for status or data
      if (response && typeof response === 'object' && 'status' in response && response.status >= 200 && response.status < 300) {
        const data = 'data' in response ? (response as any).data : undefined;
        return {
          success: true,
          data
        };
      }

      // Fallback: treat as success if no error found
      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : pushConfigsToUat\n` + 
        `\nresulted in error : \n`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  getPendingUatConfigs: async (rc: RunContextApp, projectId: string): Promise<{ success: boolean; data?: PendingUatConfig[]; error?: string }> => {
    try {
      const response = await rc.bffClient.getPendingUatConfigs(projectId);

      if (response && typeof response === 'object' && 'error' in response && response.error) {
        return {
          success: false,
          error: (response as any).error?.message || 'Failed to fetch pending configs'
        };
      }

      if (response && 'data' in response) {
        const raw: any = (response as any).data;
        const list: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        return { success: true, data: list as PendingUatConfig[] };
      }

      return { success: true, data: [] };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : pending-uat-configs\n` + 
        `\nresulted in error : \n`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  // POST Data Generator API Methods
  fetchUserDetails: async (rc: RunContextApp, requestData: {
    userUUID: string[];
    utilityId?: number; // Made optional since backend will assign it
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    houseType?: string;
    subPilot?: string;
    ingestionBucket?: string;
    fileUploadBucket: string;
    meterFuel?: string[];
    destinationPilotId?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchUserDetails(requestData);

      if ('error' in response && response.error) {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : (response.error as any)?.message || 'Unknown error occurred';
          
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        if (response.data.success === false) {
          const errorDetails = response.data.errorDetails || response.data.message || 'Failed to fetch user details';
          const errorMessage = errorDetails.split(';')[0]?.trim() || errorDetails;
          return {
            success: false,
            data: response.data,
            error: errorMessage
          };
        }
        
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error: any) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : /v1/fetchProdUserData/userDetails\n` + 
        `\nresulted in error : \n`, error);
      
      let errorMessage = 'Unknown error occurred';
      if (error?.response?.data?.errorDetails) {
        const errorDetails = error.response.data.errorDetails;
        errorMessage = errorDetails.split(';')[0]?.trim() || errorDetails;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // New GET method for userDetails
  getUserDetails: async (rc: RunContextApp, requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.getUserDetails(requestData);

      // Check if response has error property
      if ('error' in response && response.error) {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : (response.error as any)?.message || 'Unknown error occurred';
          
        return {
          success: false,
          error: errorMessage
        };
      }

      // Handle successful response
      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /v1/fetchProdUserData/userDetails\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  fetchRawData: async (rc: RunContextApp, requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchRawData(requestData);

      // Check if response has error property
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      // Handle successful response
      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /v1/fetchProdUserData/rawData\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  fetchBillingData: async (rc: RunContextApp, requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
    hasBBUserProgram?: boolean;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchBillingData(requestData);

      // Check if response has error property
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      // Handle successful response
      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /v1/fetchProdUserData/billingData\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  fetchMeterData: async (rc: RunContextApp, requestData: {
    userUUID: string[];
    utilityId?: number;
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    houseType?: string;
    subPilot?: string;
    ingestionBucket?: string;
    fileUploadBucket: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchMeterData(requestData);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : /v1/fetchProdUserData/meterData\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  saveUserMetadata: async (rc: RunContextApp, requestData: {
    first_name: string;
    last_name?: string;
    rate_plan: string;
    billing_cycle?: string;
    bb_duration?: string;
    primary_email: string;
    secondary_email?: string;
    address: string;
    meter_type?: string;
    dcx_clusters?: string;
    zipcode?: string;
    persona_name?: string;
    persona_description?: string;
    source_uuid: string;
    source_pilot_id?: string;
    destination_pilot_id?: string;
    source_env: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.saveUserMetadata(requestData);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error: any) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : /v1/user-metadata\n` + 
        `\nresulted in error : \n`, error);
      
      let errorMessage = 'Unknown error occurred';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  deleteUserMetadata: async (rc: RunContextApp, recordId: number): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.deleteUserMetadata(recordId);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> DELETE request to : /v1/user-metadata/${recordId}\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  getUserMetadata: async (rc: RunContextApp, sourceUuid: string, sourceEnv: string): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.getUserMetadata(sourceUuid, sourceEnv);

      // Check if response has error property
      if ('error' in response && response.error) {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : (response.error as any)?.message || 'Unknown error occurred';
          
        return {
          success: false,
          error: errorMessage
        };
      }

      // Handle successful response
      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /v1/user-metadata\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  // New method to fetch all user metadata without query parameters
  getAllUserMetadata: async (rc: RunContextApp, destinationPilotId?: string, status?: string): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.getAllUserMetadata(destinationPilotId, status);
      if ('error' in response) {
        rc.error(rc.getName(PilotApis),
          `Error >> GET request to : /v1/user-metadata\n` +
          `\nresulted in error : \n`, response.error);
        return { success: false, error: response.error };
      }
      return { success: true, data: response.data };
    } catch (error) {
      rc.error(rc.getName(PilotApis),
        `Error >> GET request to : /v1/user-metadata\n` +
        `\nresulted in error : \n`, error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  fetchRatePlans: async (rc: RunContextApp, apiHost: string | undefined, utilityId: string): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchRatePlans(apiHost, utilityId);

      // Check if response has error property
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      // Handle successful response
      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /v1/fetch-rates-plans\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  createBillingCycleSchedule: async (rc: RunContextApp, requestData: {
    destinationPilotId: string;
    destinationEnv: string;
    sourceUUID: string;
    sourceEnv: string;
    utilityName: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.createBillingCycleSchedule(requestData);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /v1/fetchProdUserData/createBCSchedule\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  fetchUserUUID: async (rc: RunContextApp, requestData: {
    destinationPilotId: string;
    destinationEnv: string;
    sourceEnv: string;
    sourceUUID: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchUserUUID(requestData);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> GET request to : /fetchUserUUID\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  assignShcCluster: async (rc: RunContextApp, requestData: {
    uuid: string;
    data_server_url?: string;
    destination_pilot_id: string;
  }): Promise<{ success: boolean; data?: any; error?: string | any }> => {
    try {
      const response = await rc.bffClient.assignShcCluster(requestData);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error,
          data: response.data || response.error
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error: any) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : /v1/assignShcCluster\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  // Generate web preview URL for user dashboard
  generateUserWebDashboard: async (rc: RunContextApp, uuid: string, destinationPilotId?: string, apiHost?: string): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const requestData = {
        uuid,
        ...(destinationPilotId && { destination_pilot_id: destinationPilotId }),
        ...(apiHost && { api_host: apiHost })
      };

      const response = await rc.bffClient.generateUserWebDashboard(requestData);
      
      if ('error' in response && response.error) {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : (response.error as any)?.message || 'Unknown error occurred';
          
        return {
          success: false,
          error: errorMessage
        };
      }

      // Handle successful response
      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : /v1/user-web-dashboard\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  archiveProject: async (rc: RunContextApp, projectId: string): Promise<any> => {
    try {
      const response = await rc.bffClient.archiveProject(projectId);

      // Check if response has error property
      if ('error' in response && response.error) {
        return {
          error: response.error
        };
      }

      return {
        data: (response as any).data
      };
    } catch (error) {
      rc.error(rc.getName(PilotApis), 
        `Error >> POST request to : /v1/archive-project\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
  
}